'use client';

import { useGSAP } from '@gsap/react';
import clsx from 'clsx';
import { FC, Fragment, useRef } from 'react';

import Image from '@/components/Image';
import { Typography } from '@/components/shared/Typography';
import StrapiRichContent from '@/components/StrapiRichContent';
import { gsap } from '@/lib/gsap';

import css from './style.module.scss';
import { StatsWithImageDto } from './types';

interface Props {
	data: StatsWithImageDto;
}

const StatsWithImage: FC<Props> = ({ data }) => {
	const { title, items } = data;
	const sectionRef = useRef<HTMLDivElement>(null);
	const titleRef = useRef<HTMLHeadingElement>(null);
	const cardsRef = useRef<HTMLDivElement>(null);

	useGSAP(
		() => {
			const wrapper = sectionRef.current;
			const titleEl = titleRef.current;
			const cardsEl = cardsRef.current;
			const titleLines = titleEl?.querySelectorAll('p');

			if (!wrapper || !titleEl) return;

			const mm = gsap.matchMedia();
			const titleTargets = titleLines && titleLines.length ? Array.from(titleLines) : [titleEl];

			gsap.set(titleTargets, {
				opacity: 0,
				y: 40,
				filter: 'blur(10px)'
			});

			gsap.to(titleTargets, {
				opacity: 1,
				y: 0,
				filter: 'blur(0px)',
				duration: 1.1,
				ease: 'power3.out',
				stagger: titleTargets.length > 1 ? 0.15 : 0,
				scrollTrigger: {
					trigger: titleEl,
					start: 'top 80%',
					toggleActions: 'play none none reverse'
				}
			});

			mm.add('(min-width: 992px)', () => {
				if (!cardsEl) return;

				gsap.to(titleEl, {
					yPercent: -50,
					ease: 'none',
					scrollTrigger: {
						trigger: wrapper,
						start: 'top bottom',
						end: 'bottom top',
						scrub: true
					}
				});

				gsap.to(cardsEl, {
					yPercent: 90,
					ease: 'none',
					scrollTrigger: {
						trigger: wrapper,
						start: 'top bottom',
						end: 'bottom top',
						scrub: true
					}
				});

				gsap.fromTo(
					cardsEl,
					{
						autoAlpha: 0,
						y: 80
					},
					{
						autoAlpha: 1,
						y: 0,
						duration: 1.2,
						delay: 0.2,
						ease: 'power3.out',
						scrollTrigger: {
							trigger: wrapper,
							start: 'top 70%'
						}
					}
				);
			});

			return () => {
				mm.revert();
			};
		},
		{
			scope: sectionRef
		}
	);

	return (
		<section ref={sectionRef} className={css.wrapper}>
			{title ? (
				<Typography ref={titleRef} className={css.title} variant="h2" tag="h2">
					{typeof title === 'string' ? <p>{title}</p> : <StrapiRichContent content={title} />}
				</Typography>
			) : null}

			<div
				ref={cardsRef}
				className={clsx(css.content, {
					[css.forthItems]: items.length === 4
				})}
			>
				{items?.map(({ id, image, text }, idx) => (
					<Fragment key={`${id}-${idx}`}>
						<div className={clsx(css.item, { [css.lastItem]: idx === items.length - 1 })}>
							{image ? (
								<Image
									preload
									height={90}
									width={260}
									quality={100}
									src={image?.url ?? ''}
									alt="Statistic value image"
								/>
							) : null}

							{text ? (
								<div className={css.text}>
									<StrapiRichContent content={text} />
								</div>
							) : null}
						</div>

						<div
							className={clsx(css.divider, {
								[css.lastItem]: idx === items.length - 1,
								[css.hidden]: items.length === 4 && (idx + 1) % 2 === 0
							})}
						/>
					</Fragment>
				))}
			</div>
		</section>
	);
};

export default StatsWithImage;
